import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import configurationServices from '@/configuration/services/services'

import {
  getPortalFeaturesPayload,
  getStoreInfoPayload,
  updateContextStoreInfo,
  updateLocalStorageStoreInfo,
} from './helpers'

const useStoreInfoConfiguration = ({ setToastInfo, selectedStore, updateContext, returnPortalId }) => {
  const { t } = useTranslation()
  const [contactEmail, setContactEmail] = useState('')
  const [isEmailValid, setIsEmailValid] = useState(true)
  const [defaultLanguage, setDefaultLanguage] = useState('')
  const [supportedLanguages, setSupportedLanguages] = useState([])
  const [returnsPolicyUrl, setReturnsPolicyUrl] = useState('')
  const [helpCenterUrl, setHelpCenterUrl] = useState('')
  const [contactUrl, setContactUrl] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const { fetchData: changeStoreInfo, loading } = useFetch(configurationServices.changeStoreInfo, {
    doInitialRequest: false,
  })
  const {
    returnPortalFeatures,
    refetchReturnPortalFeatures,
    fetchingReturnPortalFeatures,
    editReturnPortalPreferences,
    editingReturnPortalPreferences,
  } = useReturnPortalFeatures({
    returnPortalId,
  })

  const setInitValues = useCallback(() => {
    if (returnPortalFeatures && selectedStore) {
      setContactEmail(selectedStore?.contact_email || '')
      setDefaultLanguage(selectedStore?.default_language || 'en')
      setSupportedLanguages(selectedStore?.supported_languages || ['en'])
      setContactUrl(selectedStore?.contact_url || '')
      setReturnsPolicyUrl(returnPortalFeatures?.returns_policy_url || '')
      setHelpCenterUrl(returnPortalFeatures?.help_center_url || '')
      setHasChanges(false)
    }
  }, [returnPortalFeatures, selectedStore])

  useEffect(() => {
    setInitValues()
  }, [setInitValues])

  const resetFields = () => {
    setInitValues()
  }

  const onChangeContactEmail = (e, isValid) => {
    setHasChanges(true)
    setContactEmail(e.target.value)
    setIsEmailValid(isValid)
  }

  const onChangeSupportedLanguages = e => {
    setHasChanges(true)
    setSupportedLanguages(e.target.value)
    if (!e.target.value.includes(defaultLanguage)) setDefaultLanguage(null)
  }

  const onSave = async () => {
    try {
      const [response] = await Promise.all([
        changeStoreInfo(
          getStoreInfoPayload({
            contactEmail,
            defaultLanguage,
            supportedLanguages,
            storeId: selectedStore.id,
            contactUrl,
          })
        ),
        editReturnPortalPreferences(
          getPortalFeaturesPayload({
            returnPortalId,
            returnsPolicyUrl,
            helpCenterUrl,
          })
        ),
      ])

      updateLocalStorageStoreInfo({
        contactEmail: response?.contact_email,
        supportedLanguages: response?.supported_languages,
        defaultLanguage: response?.default_language,
      })
      updateContextStoreInfo({
        updateContext,
        contactEmail: response?.contact_email,
        supportedLanguages: response?.supported_languages,
        defaultLanguage: response?.default_language,
        contact_url: response?.contact_url,
      })
      refetchReturnPortalFeatures()
      setHasChanges(false)
      setToastInfo({ show: true, message: t('La configuración se ha guardado correctamente'), type: 'success' })
    } catch (error) {
      refetchReturnPortalFeatures()
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onChangeContactUrl = e => {
    setHasChanges(true)
    setContactUrl(e.target.value)
  }

  return {
    changeStoreInfo,
    loading: loading || fetchingReturnPortalFeatures || editingReturnPortalPreferences,
    contactEmail,
    isEmailValid,
    defaultLanguage,
    supportedLanguages,
    setContactEmail,
    setIsEmailValid,
    setDefaultLanguage: params => {
      setHasChanges(true)
      setDefaultLanguage(params)
    },
    setSupportedLanguages,
    onChangeContactEmail,
    hasChanges,
    resetFields,
    onChangeSupportedLanguages,
    helpCenterUrl,
    setHelpCenterUrl: params => {
      setHasChanges(true)
      setHelpCenterUrl(params)
    },
    returnsPolicyUrl,
    setReturnsPolicyUrl: params => {
      setHasChanges(true)
      setReturnsPolicyUrl(params)
    },
    contactUrl,
    onChangeContactUrl,
    onSave,
  }
}

export default useStoreInfoConfiguration
